import { ADDRESS_VALIDATION_API_URL } from "@constants/api";
import { useJsApiLoader } from "@react-google-maps/api";
import {
	addLocation,
	getLocationGeoLocation,
} from "@services/location-service";
import { Checkbox, Form, Input, message } from "antd";
import type { InputRef } from "antd";
import { MutableRefObject, useEffect, useRef, useState } from "react";

// Google Maps libraries (must match other components)
const libraries: ("places" | "visualization" | "geometry")[] = [
	"places",
	"visualization",
	"geometry",
];

interface AddSingleLocationProps {
	formRef: MutableRefObject<any>;
	modalOpen: boolean;
	toggleModal: () => void;
	onLocationAdded?: () => void;
}

interface AddressValues {
	name?: string;
	address1?: string;
	address2?: string;
	state?: string;
	city?: string;
	zip?: string;
	google_address: string;
	country?: string;
	store_number?: string;
}

const AddSingleLocation = ({
	formRef,
	modalOpen,
	toggleModal,
	onLocationAdded,
}: AddSingleLocationProps) => {
	const autocompleteInput = useRef<InputRef>(null);
	const [autocomplete, setAutocomplete] =
		useState<google.maps.places.Autocomplete | null>(null);
	const [confirmLoading, setConfirmLoading] = useState(false);
	const [isInternational, setIsInternational] = useState(false);
	const [inputs] = useState([
		{ label: "Location Name", responseTag: "name", required: true },
		{ label: "Store Number", responseTag: "store_number", required: true },
		{ label: "Address 1", responseTag: "address1", required: true },
		{
			label: "Address 2",
			responseTag: "address2",
			id: "address2",
			required: false,
		},
		{ label: "State", responseTag: "state", id: "state", required: true },
		{ label: "City", responseTag: "city", id: "city", required: true },
		{ label: "Zip Code", responseTag: "zip", id: "zip", required: true },
	]);
	const [error, setError] = useState<string | JSX.Element>("");
	const [form] = Form.useForm();

	// Google Maps API loader
	const { isLoaded: isGoogleMapsLoaded } = useJsApiLoader({
		googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
		libraries: libraries,
	});

	useEffect(() => {
		if (formRef) {
			formRef.current = form;
		}
	}, [formRef, form]);

	useEffect(() => {
		const initializeAutocomplete = () => {
			if (
				modalOpen &&
				autocompleteInput.current?.input &&
				!autocomplete &&
				window.google?.maps &&
				isGoogleMapsLoaded
			) {
				try {
					// Clear any existing autocomplete instance
					if (autocomplete) {
						window.google.maps.event.clearInstanceListeners(autocomplete);
						setAutocomplete(null);
					}

					const newAutocomplete = new window.google.maps.places.Autocomplete(
						autocompleteInput.current.input,
						{
							types: isInternational ? ["address"] : ["address"],
							componentRestrictions: isInternational
								? undefined
								: { country: "us" },
							fields: [
								"address_components",
								"geometry",
								"formatted_address",
								"name",
							],
						},
					);

					newAutocomplete.addListener("place_changed", () => {
						const place = newAutocomplete.getPlace();
						console.log("Selected place:", place);

						if (!place.geometry) {
							message.error(`No details available for input: '${place.name}'`);
							return;
						}

						if (isInternational) {
							// For international addresses, map the components to match US format
							const addressObj: AddressValues = {
								google_address: place.formatted_address || "",
								address1: "",
								address2: "",
								city: "",
								state: "",
								zip: "",
								country: "",
							};

							if (place.address_components) {
								console.log("Address components:", place.address_components);

								// First pass: collect all components
								const components = {
									street_number: "",
									route: "",
									postal_code: "",
									locality: "",
									administrative_area_level_1: "",
									country: "",
								};

								for (const component of place.address_components) {
									const types = component.types;
									console.log(
										"Component types:",
										types,
										"value:",
										component.long_name,
									);

									if (types.includes("street_number")) {
										components.street_number = component.long_name;
									}
									if (types.includes("route")) {
										components.route = component.long_name;
									}
									if (types.includes("postal_code")) {
										components.postal_code = component.long_name;
									}
									if (types.includes("locality")) {
										components.locality = component.long_name;
									}
									if (types.includes("administrative_area_level_1")) {
										components.administrative_area_level_1 =
											component.short_name;
									}
									if (types.includes("country")) {
										components.country = component.short_name;
									}
								}

								// Second pass: map to our format
								addressObj.address1 = [
									components.street_number,
									components.route,
								]
									.filter(Boolean)
									.join(" ");
								addressObj.zip = components.postal_code || "N/A";
								addressObj.city = components.locality || "N/A";
								addressObj.state =
									components.administrative_area_level_1 || "N/A";
								addressObj.country = components.country || "N/A";

								// If we don't have a street address, use the formatted address
								if (!addressObj.address1) {
									addressObj.address1 = place.formatted_address;
								}

								console.log("Mapped components:", components);
								console.log("Final address object:", addressObj);
							}

							if (form.getFieldValue("name")) {
								addressObj.name = form.getFieldValue("name");
							}
							if (form.getFieldValue("store_number")) {
								addressObj.store_number = form.getFieldValue("store_number");
							}

							console.log("Final address object:", addressObj);
							form.setFieldsValue(addressObj);
							return;
						}

						// US address handling
						const addressObj: AddressValues = {
							google_address: place.formatted_address || "",
						};
						let address1 = "";
						let zip = "";

						if (place.address_components) {
							for (const component of place.address_components) {
								const componentType = component.types[0];
								switch (componentType) {
									case "street_number":
										address1 = `${component.long_name} ${address1}`;
										break;
									case "subpremise":
										addressObj.address2 = `${component.long_name}`;
										break;
									case "route":
										address1 += component.long_name;
										addressObj.address1 = address1;
										break;
									case "postal_code":
										zip = `${component.long_name}${zip}`;
										addressObj.zip = zip;
										break;
									case "locality":
										addressObj.city = component.long_name;
										break;
									case "administrative_area_level_1":
										addressObj.state = component.short_name;
										break;
								}
							}
						}

						if (form.getFieldValue("name")) {
							addressObj.name = form.getFieldValue("name");
						}
						if (form.getFieldValue("store_number")) {
							addressObj.store_number = form.getFieldValue("store_number");
						}
						form.setFieldsValue(addressObj);
					});

					setAutocomplete(newAutocomplete);
				} catch (error) {
					console.error(
						"Error initializing Google Places Autocomplete:",
						error,
					);
					message.error(
						"Failed to initialize address search. Please refresh the page and try again.",
					);
				}
			}
		};

		if (isGoogleMapsLoaded) {
			initializeAutocomplete();
		}

		return () => {
			if (autocomplete) {
				try {
					window.google.maps.event.clearInstanceListeners(autocomplete);
				} catch (error) {
					console.error("Error cleaning up autocomplete:", error);
				}
				setAutocomplete(null);
			}
		};
	}, [
		modalOpen,
		autocompleteInput.current,
		autocomplete,
		form,
		isInternational,
		isGoogleMapsLoaded,
	]);

	const getValidationResponse = async (values: AddressValues) => {
		if (isInternational) {
			// Skip validation for international addresses
			return { result: { verdict: { addressComplete: true } } };
		}

		const url =
			ADDRESS_VALIDATION_API_URL +
			import.meta.env.VITE_ADDRESS_VALIDATION_API_KEY;
		let addressLines = values.address1;
		if (values.address2) addressLines += ` ${values.address2}`;

		const body = JSON.stringify({
			address: {
				regionCode: "US",
				locality: values.city,
				administrativeArea: values.state,
				postalCode: values.zip,
				addressLines: [addressLines],
			},
			previousResponseId: "",
			enableUspsCass: false,
		});

		try {
			const response = await fetch(url, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: body,
			});
			if (!response.ok) {
				throw new Error(`HTTP error ${response.status}`);
			}
			return await response.json();
		} catch (error: unknown) {
			if (error instanceof Error) {
				console.log(`Fetch error: ${error.message}`);
			}
			return false;
		}
	};

	const addLocationToDbAndCloseModal = async (address: AddressValues) => {
		try {
			const geolocation = await getLocationGeoLocation(address);
			console.log("Geolocation response:", geolocation);

			if (!geolocation.results || !geolocation.results[0]?.geometry?.location) {
				throw new Error("Failed to get geolocation for the address");
			}

			const geoLoc = geolocation.results[0].geometry.location;
			const { lat, lng } = geoLoc;

			// Ensure all required fields are present and match US format
			const locationData: Record<string, string | number> = {
				name: address.name || "",
				store_number: address.store_number || "",
				address1: address.address1 || "",
				address2: address.address2 || "",
				city: address.city || "N/A",
				state: address.state || "N/A",
				zip: address.zip || "N/A",
				google_address: address.google_address || "",
				latitude: lat,
				longitude: lng,
				country: address.country || "N/A",
			};

			// For international addresses, ensure we have the full address in address1
			if (isInternational && !locationData.address1) {
				locationData.address1 = locationData.google_address;
			}

			console.log("Sending location data:", locationData);
			const response = await addLocation(locationData);

			if (response?.status) {
				form.resetFields();
				toggleModal();
				onLocationAdded?.();
			} else {
				throw new Error("Failed to add location");
			}
		} catch (error) {
			console.error("Error adding location:", error);
			throw error;
		}
	};

	const validateAddress = async (values: AddressValues) => {
		const validationResponse = await getValidationResponse(values);

		if (!validationResponse) {
			setError(
				<span className="error-text fw-bold">
					Address validation failed. Please try again.
				</span>,
			);
			return false;
		}

		if (isInternational) {
			await addLocationToDbAndCloseModal(values);
			return true;
		}

		const { formattedAddress, missingComponentTypes } =
			validationResponse.result.address;
		const verdict = validationResponse.result.verdict;

		// Check if the address is complete enough to proceed
		if (verdict.addressComplete) {
			await addLocationToDbAndCloseModal({
				...values,
				google_address: formattedAddress,
			});
			return true;
		}

		// Handle case where address is valid but could have subpremises
		if (verdict.possibleNextAction === "CONFIRM_ADD_SUBPREMISES") {
			// Address is valid even though it's missing subpremise - we can proceed
			await addLocationToDbAndCloseModal({
				...values,
				google_address: formattedAddress,
			});
			return true;
		}

		// Only show error for missing components if they are critical (not subpremise)
		if (missingComponentTypes && missingComponentTypes.length > 0) {
			const criticalMissing = missingComponentTypes.filter(
				(type: string) => type !== "subpremise",
			);

			if (criticalMissing.length > 0) {
				setError(
					<div className="d-flex flex-column">
						<span className="error-text fw-bold">
							Address could not be validated. Please enter additional
							information for:
						</span>
						{criticalMissing.map((component: string) => (
							<span key={component} className="error-text fw-bold">
								{convertComponentTypeToLabel(component)}
							</span>
						))}
					</div>,
				);
				return false;
			} else {
				// Only missing subpremise, which is optional - proceed with address
				await addLocationToDbAndCloseModal({
					...values,
					google_address: formattedAddress,
				});
				return true;
			}
		}

		setError(
			<span className="error-text fw-bold">
				Address could not be validated. Please check for any errors and try
				again.
			</span>,
		);
		return false;
	};

	const convertComponentTypeToLabel = (type: string) => {
		switch (type) {
			case "street_number":
				return "Street Number";
			case "subpremise":
				return "Address 2";
			case "route":
				return "Address 1";
			case "postal_code":
				return "Zip Code";
			case "locality":
				return "City";
			case "administrative_area_level_1":
				return "State";
			default:
				return type;
		}
	};

	const submit = async (values: AddressValues) => {
		setConfirmLoading(true);
		try {
			const isValid = await validateAddress(values);
			if (isValid) {
				message.success("Address validated and location added successfully.");
			} else {
				message.error("Address validation failed. Please correct the errors.");
			}
		} catch (error: unknown) {
			if (error instanceof Error) {
				message.error(`${error.toString()}`);
				console.error("Submission error:", error);
			}
		} finally {
			setConfirmLoading(false);
		}
	};

	return (
		<Form form={form} layout="vertical" onFinish={submit}>
			<Form.Item>
				<Checkbox
					checked={isInternational}
					onChange={(e) => {
						setIsInternational(e.target.checked);
						form.resetFields();
					}}
				>
					International Location
				</Checkbox>
			</Form.Item>

			<Form.Item
				label="Location Name"
				name="name"
				rules={[
					{
						required: true,
						message: "Location Name is required",
					},
				]}
			>
				<Input />
			</Form.Item>

			<Form.Item
				label="Store Number"
				name="store_number"
				rules={[
					{
						required: true,
						message: "Store Number is required",
					},
				]}
			>
				<Input />
			</Form.Item>

			{isInternational ? (
				<Form.Item
					label="Full Address"
					name="google_address"
					rules={[
						{
							required: true,
							message: "Address is required",
						},
					]}
				>
					<Input
						ref={autocompleteInput}
						onFocus={() => {
							// Reinitialize autocomplete when input is focused
							if (autocomplete) {
								window.google.maps.event.clearInstanceListeners(autocomplete);
								setAutocomplete(null);
							}
						}}
					/>
				</Form.Item>
			) : (
				inputs
					.filter(
						(input) =>
							input.responseTag !== "name" &&
							input.responseTag !== "store_number",
					)
					.map((input) => (
						<Form.Item
							key={input.responseTag}
							label={input.label}
							name={input.responseTag}
							rules={[
								{
									required: input.required,
									message: `${input.label} is required`,
								},
							]}
						>
							<Input
								id={input.id}
								ref={
									input.responseTag === "address1" ? autocompleteInput : null
								}
								onFocus={() => {
									// Reinitialize autocomplete when input is focused
									if (autocomplete) {
										window.google.maps.event.clearInstanceListeners(
											autocomplete,
										);
										setAutocomplete(null);
									}
								}}
							/>
						</Form.Item>
					))
			)}
			{error}
		</Form>
	);
};

export { AddSingleLocation };
